import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Save, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Belongings() {
  const navigate = useNavigate();
  const [instructions, setInstructions] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadInstructions();
  }, []);

  const loadInstructions = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      navigate('/');
      return;
    }

    try {
      const response = await axios.get(`${BACKEND_URL}/api/belongings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInstructions(response.data.instructions || '');
    } catch (error) {
      console.error('Failed to load instructions:', error);
      toast.error('Failed to load instructions');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const token = localStorage.getItem('session_token');
    setSaving(true);

    try {
      await axios.post(
        `${BACKEND_URL}/api/belongings`,
        { instructions },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Instructions saved successfully');
    } catch (error) {
      console.error('Failed to save instructions:', error);
      toast.error('Failed to save instructions');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#EBF0F5] via-[#EBF0F5] to-[#EBF0F5]">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-[#2B4C7E] border-r-transparent mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#EBF0F5] via-[#EBF0F5] to-[#EBF0F5]" data-testid="belongings-page">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-[#EBF0F5] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="rounded-full flex-shrink-0">
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              </Link>
              <h1 className="text-lg sm:text-2xl font-medium bg-gradient-to-r from-[#2B4C7E] to-[#2B4C7E] bg-clip-text text-transparent truncate">Belongings</h1>
            </div>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-gradient-to-r from-[#2B4C7E] to-[#2B4C7E] text-white rounded-full hover:from-[#1E3A63] hover:to-[#1E3A63] flex-shrink-0 text-xs sm:text-sm px-3 sm:px-4"
              data-testid="save-belongings-btn"
            >
              <Save className="h-4 w-4 sm:h-5 sm:w-5 sm:mr-2" />
              <span className="hidden sm:inline">{saving ? 'Saving...' : 'Save Instructions'}</span>
              <span className="sm:hidden">{saving ? '...' : 'Save'}</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto p-4 md:p-8">
        <Card className="p-8 rounded-2xl border border-[#EBF0F5] bg-white/90 backdrop-blur-md shadow-lg">
          <div className="flex items-start gap-6 mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-[#EBF0F5] to-[#D7E0EA] rounded-xl flex items-center justify-center flex-shrink-0">
              <Package className="h-8 w-8 text-[#2B4C7E]" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="text-2xl font-medium text-gray-800 mb-2">What should happen to your belongings?</h2>
              <p className="text-gray-600">
                Provide detailed instructions about what you want done with your possessions, property, and personal items after your passing.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">Instructions</label>
            <Textarea
              placeholder="Example:

• My vintage car collection should go to my son, Michael
• The family home at 123 Oak Street should be sold and proceeds divided equally among all children
• My book collection should be donated to the public library
• Jewelry box in the master bedroom closet goes to my daughter, Sarah
• All furniture can be divided among family members or donated to charity
• Personal photos and albums should be digitized and shared with all family members

Be as specific as possible to avoid confusion and ensure your wishes are carried out."
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              rows={20}
              className="font-mono text-sm"
              data-testid="belongings-textarea"
            />
          </div>
        </Card>

        {/* Guidelines Card */}
        <Card className="mt-8 p-6 rounded-2xl border border-[#EBF0F5] bg-gradient-to-br from-[#EBF0F5] to-white">
          <h3 className="text-lg font-medium text-gray-800 mb-3">Guidelines for Writing Instructions</h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start">
              <span className="text-[#2B4C7E] mr-2">•</span>
              <span><strong>Be Specific:</strong> Include exact locations and descriptions</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#2B4C7E] mr-2">•</span>
              <span><strong>Name Recipients:</strong> Clearly state who should receive what</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#2B4C7E] mr-2">•</span>
              <span><strong>Include Details:</strong> Mention sentimental items and their significance</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#2B4C7E] mr-2">•</span>
              <span><strong>Real Estate:</strong> Provide property addresses and your wishes for them</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#2B4C7E] mr-2">•</span>
              <span><strong>Digital Assets:</strong> Don't forget about online accounts and digital files</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#2B4C7E] mr-2">•</span>
              <span><strong>Update Regularly:</strong> Review and update these instructions as circumstances change</span>
            </li>
          </ul>
        </Card>
      </main>
    </div>
  );
}